import { IndianRupee, AlertTriangle, BarChart2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export default function VendorReportsTab({ vendors, getReportData, getAlerts, getVendorPerformance, getStatusBadgeClass }) {
  const reportData = getReportData();
  const alerts = getAlerts();
  const performance = getVendorPerformance();
  const allTransactions = vendors
    .flatMap((vendor) => vendor.transactions.map((t) => ({ ...t, vendorName: vendor.name })))
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 10);

  return (
    <>
      {/* Analytics Cards */}
      <Card className="border-0 shadow-lg rounded-xl overflow-hidden mb-6">
        <CardHeader className="bg-[#0263cc]/5 border-b border-[#0263cc]/10 pb-4">
          <CardTitle className="text-[#0263cc] text-xl font-semibold">Reports & Analytics</CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-8">
            {/* Payment Summary */}
            <Card className="shadow-sm">
              <CardContent className="p-4 md:p-6">
                <h3 className="text-lg font-medium mb-4 flex items-center">
                  <IndianRupee className="h-5 w-5 mr-2 text-green-600" /> Payment Summary
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Paid:</span>
                    <span className="font-medium text-green-700">₹{reportData.paymentReport.reduce((sum, v) => sum + v.totalPaid, 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Partial (unpaid balance):</span>
                    <span className="font-medium text-yellow-600">₹{reportData.paymentReport.reduce((sum, v) => sum + v.totalPartial, 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Pending:</span>
                    <span className="font-medium text-red-600">₹{reportData.paymentReport.reduce((sum, v) => sum + v.totalPending, 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between border-t pt-2 mt-1">
                    <span className="text-gray-700 font-medium">Total Outstanding:</span>
                    <span className="font-semibold text-red-700">₹{reportData.paymentReport.reduce((sum, v) => sum + v.totalPending + v.totalPartial, 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Active Vendors:</span>
                    <span className="font-medium">{vendors.filter((v) => v.status === "active").length}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Alerts */}
            <Card className="shadow-sm">
              <CardContent className="p-4 md:p-6">
                <h3 className="text-lg font-medium mb-4 flex items-center">
                  <AlertTriangle className="h-5 w-5 mr-2 text-orange-600" /> Alerts
                </h3>
                <div className="space-y-3">
                  {alerts.length === 0 ? (
                    <div className="text-center py-4 text-gray-500 text-sm">No alerts at this time</div>
                  ) : (
                    alerts.slice(0, 5).map((alert, index) => (
                      <div key={index} className={`flex items-start p-2 rounded ${alert.severity === "high" ? "bg-red-50" : "bg-orange-50"}`}>
                        {alert.type === "contract" ? (
                          <AlertTriangle className={`h-4 w-4 mt-0.5 mr-2 flex-shrink-0 ${alert.severity === "high" ? "text-red-600" : "text-orange-600"}`} />
                        ) : (
                          <IndianRupee className={`h-4 w-4 mt-0.5 mr-2 flex-shrink-0 ${alert.severity === "high" ? "text-red-600" : "text-orange-600"}`} />
                        )}
                        <div>
                          <div className="font-medium">{alert.vendorName}</div>
                          <div className={`text-sm ${alert.severity === "high" ? "text-red-700" : "text-orange-700"}`}>{alert.message}</div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Vendor Performance */}
            <Card className="shadow-sm md:col-span-2 lg:col-span-1">
              <CardContent className="p-4 md:p-6">
                <h3 className="text-lg font-medium mb-4 flex items-center">
                  <BarChart2 className="h-5 w-5 mr-2 text-[#0263cc]" /> Vendor Performance
                </h3>
                <div className="space-y-4">
                  {performance.length === 0 ? (
                    <div className="text-center py-4 text-gray-500 text-sm">No performance data available</div>
                  ) : (
                    performance.map((vendor, index) => (
                      <div key={index} className="space-y-1">
                        <div className="flex justify-between items-center">
                          <span className="font-medium truncate pr-2">{vendor.vendorName}</span>
                          <span className="text-sm whitespace-nowrap">{vendor.onTimePercentage}% on-time</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-[#0263cc] h-2 rounded-full" style={{ width: `${vendor.onTimePercentage}%` }} />
                        </div>
                        <div className="text-sm text-gray-500">Quality Rating: {vendor.qualityRating > 0 ? `${vendor.qualityRating}/5` : "N/A"}</div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>

      {/* Payment Report */}
      <Card className="border-0 shadow-lg rounded-xl overflow-hidden mb-6">
        <CardHeader className="bg-[#0263cc]/5 border-b border-[#0263cc]/10 pb-4">
          <CardTitle className="text-[#0263cc] text-xl font-semibold">Vendor Payment Report</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="block md:hidden space-y-4 p-4">
            {reportData.paymentReport.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No payment data available</div>
            ) : (
              reportData.paymentReport.map((report, index) => (
                <div key={index} className="border rounded-lg p-4 bg-white shadow-sm">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-medium">{report.vendorName}</h3>
                    <span className={getStatusBadgeClass(report.totalPending === 0 && report.totalPartial === 0 ? "paid" : "pending")}>
                      {report.totalPending === 0 && report.totalPartial === 0 ? "All Cleared" : "Payment Due"}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-y-2 text-sm">
                    <div className="text-gray-500">Total Paid:</div><div className="text-green-700">₹{report.totalPaid.toLocaleString()}</div>
                    <div className="text-gray-500">Partial:</div><div className="text-yellow-600">₹{report.totalPartial.toLocaleString()}</div>
                    <div className="text-gray-500">Pending:</div><div className="text-red-700">₹{report.totalPending.toLocaleString()}</div>
                    <div className="text-gray-500">Last Transaction:</div><div>{report.lastTransactionDate}</div>
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-y border-gray-200">
                  {["Vendor", "Total Paid", "Partial", "Pending", "Last Transaction", "Status"].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {reportData.paymentReport.length === 0 ? (
                  <tr><td colSpan="6" className="px-4 py-8 text-center text-gray-500">No payment data available</td></tr>
                ) : (
                  reportData.paymentReport.map((report, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-4 font-medium">{report.vendorName}</td>
                      <td className="px-4 py-4 text-green-700">₹{report.totalPaid.toLocaleString()}</td>
                      <td className="px-4 py-4 text-yellow-600">₹{report.totalPartial.toLocaleString()}</td>
                      <td className="px-4 py-4 text-red-700">₹{report.totalPending.toLocaleString()}</td>
                      <td className="px-4 py-4">{report.lastTransactionDate}</td>
                      <td className="px-4 py-4">
                        <span className={getStatusBadgeClass(report.totalPending === 0 && report.totalPartial === 0 ? "paid" : "pending")}>
                          {report.totalPending === 0 && report.totalPartial === 0 ? "All Cleared" : "Payment Due"}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Recent Transactions */}
      <Card className="border-0 shadow-lg rounded-xl overflow-hidden">
        <CardHeader className="bg-[#0263cc]/5 border-b border-[#0263cc]/10 pb-4">
          <CardTitle className="text-[#0263cc] text-xl font-semibold">Recent Transactions</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="block md:hidden space-y-4 p-4">
            {allTransactions.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No transaction data available</div>
            ) : (
              allTransactions.map((transaction, index) => (
                <div key={index} className="border rounded-lg p-4 bg-white shadow-sm">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="font-medium">{transaction.id}</h3>
                      <div className="text-sm text-gray-600">{transaction.vendorName}</div>
                    </div>
                    <span className={getStatusBadgeClass(transaction.status)}>
                      {transaction.status === "paid" ? "Paid" : transaction.status === "partial" ? "Partial" : "Pending"}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-y-2 text-sm">
                    <div className="text-gray-500">Date:</div><div>{new Date(transaction.date).toLocaleDateString()}</div>
                    <div className="text-gray-500">Amount:</div><div>₹{transaction.amount.toLocaleString()}</div>
                    <div className="text-gray-500">Delivery:</div>
                    <div><span className={getStatusBadgeClass(transaction.deliveryOnTime ? "active" : "inactive")}>{transaction.deliveryOnTime ? "On-Time" : "Delayed"}</span></div>
                    {transaction.notes && (<><div className="text-gray-500">Notes:</div><div>{transaction.notes}</div></>)}
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-y border-gray-200">
                  {["Transaction ID", "Vendor", "Date", "Amount", "Status", "On-Time Delivery", "Notes"].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {allTransactions.length === 0 ? (
                  <tr><td colSpan="7" className="px-4 py-8 text-center text-gray-500">No transaction data available</td></tr>
                ) : (
                  allTransactions.map((transaction, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-4">{transaction.id}</td>
                      <td className="px-4 py-4">{transaction.vendorName}</td>
                      <td className="px-4 py-4">{new Date(transaction.date).toLocaleDateString()}</td>
                      <td className="px-4 py-4">₹{transaction.amount.toLocaleString()}</td>
                      <td className="px-4 py-4"><span className={getStatusBadgeClass(transaction.status)}>{transaction.status === "paid" ? "Paid" : transaction.status === "partial" ? "Partial" : "Pending"}</span></td>
                      <td className="px-4 py-4"><span className={getStatusBadgeClass(transaction.deliveryOnTime ? "active" : "inactive")}>{transaction.deliveryOnTime ? "On-Time" : "Delayed"}</span></td>
                      <td className="px-4 py-4 text-sm">{transaction.notes || "-"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="p-4 border-t border-gray-200 bg-gray-50">
            <div className="text-sm text-gray-600">Showing last {allTransactions.length} transactions</div>
          </div>
        </CardContent>
      </Card>
    </>
  );
}
